<script setup lang="ts">
/**
 * 档位行编辑器 —— 一维档位表(重量 / 距离 / 体积,锚地内外各一套)的录入面。
 *
 * ── 它是哑的,这一条是硬约束 ────────────────────────────────
 * 它不认识 kg / 元 / m³:拿到的是两串文本,还回去的是两串文本。
 * 单位换算、精度校验、错误文案全部留在视图(PricingTablesView.vue)里 ——
 * 共享包那几个精确十进制解析函数只住在那边,它们的提交口径
 * 是「解析不了就拒,不猜」(M-14、Y-59)。组件里再做一份换算,同一件事就有两个源:
 * 改一处另一处不会跟着动,而两边渲染出来都正常。
 *
 * ── 它替人显示的那一件平时看不见的事 ────────────────────────
 * 契约里没有「下界」这一栏:每档的下界就是上一档的上界。人在文本框里逐行写的时候
 * 看不见这件事,造出来的空档不会报错,那段货会被静默算进上一档。区间列因此是
 * **只读的推导**(纯文本拼出来,不 parseFloat),上界乱序时这里能第一眼看出来 ——
 * 但组件不排序、不校验:排序会改变哪笔钱对应哪段货,那是视图和人的事。
 *
 * ── 事件 ────────────────────────────────────────────────────
 * change(i, field, value)  某一格被改动(值原样上抛,不 trim、不转换)
 * blur(i, field)           某一格失焦(视图据此跑一次整表校验)
 * add()                    追加一档(初值由视图给)
 * remove(i)                删掉第 i 档(只剩一档时按钮禁用 —— 一张没有档位的表算不出任何一单)
 *
 * ── 错误怎么呈现 ────────────────────────────────────────────
 * errors 同时喂两处:错的那一格描红(描红只说明「这里不对」),
 * 表下方再按「第 N 档:…」逐条列出 message(说明「为什么不对」)——
 * 只描红不给原因时,人得自己去猜文案,而这一维的单位口径有六种。
 * 组件不生成任何一句错误文案:message 原样来自视图。
 *
 * focusCell(i, field) 父组件用来把光标送到某一格:提交前定位第一个错格,或加档后落到新行。
 */
import { computed, nextTick, shallowRef } from 'vue'
import { NButton, NInput } from 'naive-ui'

/** 一行里可编辑的那两列 */
type TierField = 'bound' | 'amount'

export interface TierRow {
  bound: string
  amount: string
}

export interface TierRowError {
  field: TierField
  message: string
}

const props = defineProps<{
  /** 这一维的名字,如「重量档」 */
  title: string
  /** 上界的单位,如 kg / 公里 / m³ —— 只用来显示,不参与任何换算 */
  boundUnit: string
  /** 金额的单位,如 元 */
  amountUnit: string
  rows: TierRow[]
  /** 键是行下标;缺失表示该行没问题 */
  errors: Record<number, TierRowError>
  /** 表格下方那句业务提示 */
  hint: string
}>()

const emit = defineEmits<{
  add: []
  remove: [index: number]
  change: [index: number, field: TierField, value: string]
  blur: [index: number, field: TierField]
}>()

/**
 * 每格一个引用,键是「行下标-字段」。只用来聚焦,不参与渲染,所以不做深层响应式。
 *
 * 两种场景都靠它:
 *   ① 提交前定位 —— 视图把第一个错格标红之后调 focusCell:
 *      错隔了十行的那一格只标红不聚焦,人得自己滚着找;
 *   ② 加档之后 —— 视图 push 完一档立刻调 focusCell(新行下标),
 *      光标直接落在新行的上界格,不用人再点一下。
 */
const inputRefs = shallowRef<Record<string, InstanceType<typeof NInput> | null>>({})

function setRef(index: number, field: TierField, el: unknown): void {
  inputRefs.value[`${index}-${field}`] = (el as InstanceType<typeof NInput> | null) ?? null
}

/**
 * 把光标送到某一格。
 *
 * 那一行可能还没渲染出来(刚加档、刚切换锚地内外,视图调过来时 DOM 还是上一轮的),
 * 此时引用表里没有这个键 —— 等一轮渲染再取。第二次仍取不到说明这一行在这期间被删了,
 * 安静返回:聚焦是锦上添花,而它失败时用户看到的只是「光标还在原处」,没有别的后果。
 */
async function focusCell(index: number, field: TierField): Promise<void> {
  const key = `${index}-${field}`
  if (inputRefs.value[key] == null) await nextTick()
  inputRefs.value[key]?.focus()
}

defineExpose({ focusCell })

/** 上一档的上界文本;没有更早的非空行时是 '0'。纯文本,不 parseFloat */
function lowerBoundOf(index: number): string {
  for (let i = index - 1; i >= 0; i--) {
    const bound = (props.rows[i]?.bound ?? '').trim()
    if (bound !== '') return bound
  }
  return '0'
}

/** 区间列的展示文本。空行给「未填」,不显示一个看起来像 0 的区间 */
function rangeOf(index: number): string {
  const own = (props.rows[index]?.bound ?? '').trim()
  if (own === '') return '未填'
  return `${lowerBoundOf(index)} – ${own} ${props.boundUnit}`
}

/**
 * 表下方的错误汇总:每条错误一行,按行下标升序(人从上往下改)。
 * 文案是视图给的 message,这里只补「第 N 档」的定位前缀。
 */
const errorLines = computed(() => {
  const lines = Object.entries(props.errors).map(([index, error]) => ({
    index: Number(index),
    message: error.message,
  }))
  lines.sort((a, b) => a.index - b.index)
  return lines
})

/** 标题右侧的汇总:填了几档、覆盖到哪、有没有没改完的格子 */
const summary = computed(() => {
  const filled = props.rows.filter((r) => r.bound.trim() !== '')
  if (filled.length === 0) return '还没有填档位'
  const last = filled[filled.length - 1]?.bound.trim() ?? ''
  const pending = Object.keys(props.errors).length > 0 ? ' · 有未改完的格子' : ''
  return `共 ${filled.length} 档 · 覆盖 0–${last} ${props.boundUnit}${pending}`
})
</script>

<template>
  <div class="tier-block">
    <div class="tier-head">
      <span class="tier-title">{{ title }}</span>
      <span class="tier-summary">{{ summary }}</span>
    </div>

    <div class="tier-grid-head">
      <span>上界 {{ boundUnit }}</span>
      <span>金额 {{ amountUnit }}</span>
      <span>区间(自动)</span>
      <span></span>
    </div>

    <div v-for="(row, i) in rows" :key="i" class="tier-row">
      <n-input
        :ref="(el) => setRef(i, 'bound', el)"
        size="small"
        :value="row.bound"
        :status="errors[i]?.field === 'bound' ? 'error' : undefined"
        @update:value="emit('change', i, 'bound', $event)"
        @blur="emit('blur', i, 'bound')"
      />
      <n-input
        :ref="(el) => setRef(i, 'amount', el)"
        size="small"
        :value="row.amount"
        :status="errors[i]?.field === 'amount' ? 'error' : undefined"
        @update:value="emit('change', i, 'amount', $event)"
        @blur="emit('blur', i, 'amount')"
      />
      <span class="tier-range">{{ rangeOf(i) }}</span>
      <n-button text size="small" :disabled="rows.length <= 1" @click="emit('remove', i)">删除</n-button>
    </div>

    <!-- 红格只说「这里不对」,这一块说「为什么不对」;每条一行,按行下标升序 -->
    <div v-if="errorLines.length > 0" class="tier-errors">
      <span v-for="line in errorLines" :key="line.index" class="tier-error">
        第 {{ line.index + 1 }} 档:{{ line.message }}
      </span>
    </div>

    <n-button class="tier-add" size="small" @click="emit('add')">加一档</n-button>
    <span class="tier-hint">{{ hint }}</span>
  </div>
</template>

<style scoped>
.tier-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tier-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.tier-title {
  font-size: 13px;
  color: var(--sd-text2);
}
.tier-summary {
  font-size: 12px;
  color: var(--sd-text3);
  font-variant-numeric: tabular-nums;
}
.tier-grid-head,
.tier-row {
  display: grid;
  grid-template-columns: 96px 112px minmax(120px, 1fr) 28px;
  gap: 8px;
  align-items: center;
}
.tier-grid-head {
  font-size: 12px;
  color: var(--sd-text3);
}
.tier-range {
  font-family: var(--sd-font-mono);
  font-size: 12px;
  color: var(--sd-text3);
  font-variant-numeric: tabular-nums;
}
.tier-hint {
  font-size: 12px;
  color: var(--sd-text3);
}
/* 错误说明:危险色走令牌,不写裸色值;一行一条,长文案换行不截断 */
.tier-errors {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.tier-error {
  font-size: 12px;
  word-break: break-word;
  color: var(--sd-danger);
}
/* 外层是纵向 flex:不加这一句按钮会被拉成整行宽。提示另起一行,它是整张表的业务说明 */
.tier-add {
  align-self: flex-start;
}
</style>
